import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { Plus, X, Search, Package, Tag, DollarSign, Pencil, Trash2 } from 'lucide-react';
import axios from 'axios';
import Logo from './Logo';

import { BACKEND_URL } from "../lib/backendUrl";
const API = `${BACKEND_URL}/api`;

const UNITS = ['hour', 'project', 'item', 'month', 'year', 'unit', 'session', 'page', 'word'];

const ProductsCatalog = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', category: '', price: 0, unit: '' });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchProducts = useCallback(async () => {
    try {
      const params = {};
      if (categoryFilter !== 'all') params.category = categoryFilter;
      if (searchQuery) params.search = searchQuery;
      const res = await axios.get(`${API}/products/`, { headers, params });
      setProducts(res.data);
    } catch { /* */ } finally { setLoading(false); }
  }, [token, categoryFilter, searchQuery]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/products/categories`, { headers });
      setCategories(res.data);
    } catch { /* */ }
  }, [token]);

  useEffect(() => { fetchProducts(); fetchCategories(); }, [fetchProducts, fetchCategories]);

  const handleSave = async () => {
    if (!form.name.trim()) { toast({ title: 'Name is required', variant: 'destructive' }); return; }
    try {
      if (editing) {
        await axios.put(`${API}/products/${editing}`, form, { headers });
        toast({ title: 'Product updated' });
      } else {
        await axios.post(`${API}/products/`, form, { headers });
        toast({ title: 'Product added!' });
      }
      setShowForm(false);
      setEditing(null);
      setForm({ name: '', description: '', category: '', price: 0, unit: '' });
      fetchProducts();
      fetchCategories();
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail, variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/products/${id}`, { headers });
      toast({ title: 'Product deleted' });
      fetchProducts();
      fetchCategories();
    } catch { /* */ }
  };

  const openEdit = (p) => {
    setForm({ name: p.name, description: p.description, category: p.category, price: p.price, unit: p.unit });
    setEditing(p.id);
    setShowForm(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0d1f35] to-[#0a1628]" data-testid="products-page">
      <header className="border-b border-white/5 bg-[#0a1628]/80 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo linkTo="/dashboard" />
            <div className="h-6 w-px bg-white/10" />
            <h1 className="text-xl font-bold text-white">Products & Services</h1>
          </div>
          <Button onClick={() => { setForm({ name: '', description: '', category: '', price: 0, unit: '' }); setEditing(null); setShowForm(true); }} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="add-product-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Product
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-6" data-testid="product-stats">
          <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-white">{products.length}</p>
            <p className="text-[10px] text-gray-500">Total Products</p>
          </div>
          <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-white">{categories.length}</p>
            <p className="text-[10px] text-gray-500">Categories</p>
          </div>
          <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-white">${products.length > 0 ? (products.reduce((s, p) => s + p.price, 0) / products.length).toFixed(0) : 0}</p>
            <p className="text-[10px] text-gray-500">Avg Price</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search products..." className="bg-gray-900/50 border-white/10 text-white pl-10 h-9" data-testid="product-search" />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="bg-gray-900/50 border-white/10 text-white w-40 h-9"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent className="bg-gray-900 border-white/10">
              <SelectItem value="all" className="text-white">All Categories</SelectItem>
              {categories.map(c => <SelectItem key={c.name} value={c.name} className="text-white">{c.name} ({c.count})</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Product Grid */}
        {loading ? (
          <div className="text-center py-16 text-gray-400">Loading...</div>
        ) : products.length === 0 ? (
          <Card className="bg-gray-900/50 border-white/10">
            <CardContent className="py-16 text-center">
              <Package className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2" data-testid="no-products-msg">No products yet</h3>
              <p className="text-gray-400 mb-4">Add your products and services to quickly add them to invoices.</p>
              <Button onClick={() => setShowForm(true)} className="bg-purple-600 hover:bg-purple-700 text-white"><Plus className="w-4 h-4 mr-2" /> Add Product</Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="product-grid">
            {products.map(p => (
              <div key={p.id} className="bg-gray-900/50 border border-white/10 hover:border-purple-500/30 rounded-lg p-4 transition-all" data-testid={`product-${p.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="text-white font-medium text-sm">{p.name}</h4>
                    {p.category && <span className="text-[10px] px-2 py-0.5 bg-purple-500/10 text-purple-400 rounded-full">{p.category}</span>}
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(p)} className="text-gray-500 hover:text-white h-7 w-7 p-0"><Pencil className="w-3 h-3" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(p.id)} className="text-gray-500 hover:text-red-400 h-7 w-7 p-0"><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
                {p.description && <p className="text-gray-500 text-xs mb-3 line-clamp-2">{p.description}</p>}
                <div className="flex items-center justify-between">
                  <span className="text-white font-bold">${p.price.toFixed(2)}</span>
                  {p.unit && <span className="text-gray-500 text-xs">per {p.unit}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Add/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-6" data-testid="product-form-modal">
          <Card className="bg-gray-900 border-white/10 w-full max-w-md">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">{editing ? 'Edit Product' : 'Add Product'}</CardTitle>
                <Button size="sm" variant="ghost" onClick={() => { setShowForm(false); setEditing(null); }} className="text-gray-400 h-8 w-8 p-0"><X className="w-4 h-4" /></Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div><Label className="text-gray-400 text-xs">Name *</Label><Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="Product or service name" className="bg-black/30 border-white/10 text-white" data-testid="product-name-input" /></div>
              <div><Label className="text-gray-400 text-xs">Description</Label><Textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} placeholder="Description..." className="bg-black/30 border-white/10 text-white min-h-[60px]" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-gray-400 text-xs">Category</Label><Input value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))} placeholder="e.g. Services" className="bg-black/30 border-white/10 text-white" /></div>
                <div>
                  <Label className="text-gray-400 text-xs">Unit</Label>
                  <Select value={form.unit} onValueChange={v => setForm(f => ({...f, unit: v}))}>
                    <SelectTrigger className="bg-black/30 border-white/10 text-white"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent className="bg-gray-900 border-white/10">
                      {UNITS.map(u => <SelectItem key={u} value={u} className="text-white capitalize">{u}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label className="text-gray-400 text-xs">Price</Label><Input type="number" min="0" step="0.01" value={form.price} onChange={e => setForm(f => ({...f, price: parseFloat(e.target.value) || 0}))} className="bg-black/30 border-white/10 text-white" data-testid="product-price-input" /></div>
              <Button onClick={handleSave} className="w-full bg-purple-600 hover:bg-purple-700 text-white" data-testid="save-product-btn">{editing ? 'Update' : 'Add Product'}</Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ProductsCatalog;
